import React from 'react';
import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';
import {
  Inbox,
  ArrowDownRight,
  Layers,
  Landmark,
  Users,
  FileSpreadsheet,
  FileClock,
  ShieldCheck,
} from 'lucide-react';

export const AdminLayout: React.FC = () => {
  const { isStaff, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return <div className="py-12 text-center text-sm text-gray-400">Verifying staff credentials...</div>;
  }

  if (!isStaff) {
    return <Navigate to="/" replace />;
  }

  const tabs = [
    { to: '/admin/queue', label: t('admin.pendingQueue'), icon: Inbox },
    { to: '/admin/expenses', label: t('admin.expenses'), icon: ArrowDownRight },
    { to: '/admin/land', label: t('admin.landPayments'), icon: Layers },
    { to: '/admin/reconciliation', label: t('admin.reconciliation'), icon: Landmark },
    { to: '/admin/members', label: t('admin.members'), icon: Users },
    { to: '/admin/legacy', label: t('admin.legacyImport'), icon: FileSpreadsheet },
    { to: '/admin/audit', label: t('admin.auditLogs'), icon: FileClock },
  ];

  return (
    <div className="space-y-6 pb-24 sm:pb-8">
      {/* Admin Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 rounded-2xl bg-gradient-to-r from-navy-900 to-navy-950 text-white border border-navy-800 shadow-md">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-400 mb-1">
            <ShieldCheck className="w-4 h-4" />
            <span>Authorized Financial Personnel Only</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold">{t('admin.title')}</h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-0.5">{t('admin.subtitle')}</p>
        </div>
      </div>

      {/* Tabs Subnavigation */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none border-b border-gray-200 dark:border-navy-800">
        {tabs.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-primary-600 text-white shadow-sm shadow-primary-600/30'
                  : 'bg-white dark:bg-navy-900 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-800 border border-gray-200/80 dark:border-navy-800'
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </NavLink>
        ))}
      </div>

      {/* Nested Admin Page Outlet */}
      <Outlet />
    </div>
  );
};
